import React, { useContext, useEffect, useState } from 'react';
import { Box, Button } from '@mui/material';
import EditButton from '../../Common/Buttons/EditButton';
import { Colors } from '../../../utils/theme';
import { Actions } from '../../../enums/ActionEnums';
import { AppContext } from '../../../AppContext';
import { canUserEdit } from '../../../utils/rightUtils';
import { Types } from '../../../enums/TypeEnums';
import { InvoiceStatus } from '../../../enums/InvoiceStatusEnums';
import { Call, Close, HomeWork, LocationOn, Print, Receipt, Save } from '@mui/icons-material';
import { IPaymentDetails, useAddPaymentDetailsMutation } from '../../../generated/graphql';
import { predefinedFonts } from '../../../utils/fontStyles';
import InputComponent from '../../Common/Fields/InputComponent';
import { getLabelColor } from '../../../utils/styleUtils';

export type IErrors = {
  contactAddressName: string | null;
  contactAddress: string | null;
  contactPhone: string | null;
  contactFax: string | null;
  contactVat: string | null;
  bankName: string | null;
  bankAffiliate: string | null;
  bankAddress: string | null;
  bankBic: string | null;
  bankIban: string | null;
};

export const ErrorTexts = {
  contactAddressName: 'Contact address name is required',
  contactAddress: 'Contact address is required',
  contactPhone: 'Contact phone number is required',
  contactPhoneFormat: 'Contact phone should start with +',
  contactFax: 'Contact fax is required',
  contactFaxFormat: 'Contact fax should start with +',
  contactVat: 'VAT number is required',
  bankName: 'Bank is required',
  bankAffiliate: 'Bank affiliate is required',
  bankAddress: 'Bank address is required',
  bankBic: 'Bank BIC is required',
  bankIban: 'Bank IBAN is required'
};

const defaultErrorsObj: IErrors = {
  contactAddressName: null,
  contactAddress: null,
  contactPhone: null,
  contactFax: null,
  contactVat: null,
  bankName: null,
  bankAffiliate: null,
  bankAddress: null,
  bankBic: null,
  bankIban: null
};

const defaultPaymentDto: IPaymentDetails = {
  id: '',
  contact_address_name: '',
  contact_address: '',
  contact_phone: '',
  contact_fax: '',
  contact_vat: '',
  bank_name: '',
  bank_affiliate: '',
  bank_address: '',
  bank_bic: '',
  bank_iban: ''
};

interface IProps {
  paymentDetailsInput?: IPaymentDetails;
  updateDetails?: (updatedDetails: IPaymentDetails) => void;
}

const PaymentDetailsStep: React.FunctionComponent<IProps> = ({
  paymentDetailsInput,
  updateDetails
}: IProps) => {
  const { dispatch, state } = useContext(AppContext);
  const [addPaymentDetails, addPaymentDetailsResult] = useAddPaymentDetailsMutation();
  const [originalPaymentDetails, setOriginalPaymentDetails] = useState(
    (state.selectedInvoice?.paymentDetails as IPaymentDetails) ||
      paymentDetailsInput ||
      defaultPaymentDto
  );
  const [editable, setEditable] = useState(false);
  const [addressName, setAddressName] = useState(originalPaymentDetails.contact_address_name);
  const [address, setAddress] = useState(originalPaymentDetails.contact_address);
  const [phone, setPhone] = useState(originalPaymentDetails.contact_phone);
  const [fax, setFax] = useState(originalPaymentDetails.contact_fax);
  const [vat, setVat] = useState(originalPaymentDetails.contact_vat);
  const [bankName, setBankName] = useState(originalPaymentDetails.bank_name);
  const [bankAffiliate, setBankAffiliate] = useState(originalPaymentDetails.bank_affiliate);
  const [bankAddress, setBankAddress] = useState(originalPaymentDetails.bank_address);
  const [bankBic, setBankBic] = useState(originalPaymentDetails.bank_bic);
  const [bankIban, setBankIban] = useState(originalPaymentDetails.bank_iban);
  const [formErrors, setFormErrors] = useState(defaultErrorsObj);
  const canEdit = canUserEdit(state.user, Types.Invoice);

  useEffect(() => {
    if (paymentDetailsInput) {
      setAddressName(paymentDetailsInput.contact_address_name);
      setAddress(paymentDetailsInput.contact_address);
      setPhone(paymentDetailsInput.contact_phone);
      setFax(paymentDetailsInput.contact_fax);
      setVat(paymentDetailsInput.contact_vat);
      setBankName(paymentDetailsInput.bank_name);
      setBankAffiliate(paymentDetailsInput.bank_affiliate);
      setBankAddress(paymentDetailsInput.bank_address);
      setBankBic(paymentDetailsInput.bank_bic);
      setBankIban(paymentDetailsInput.bank_iban);
    }
  }, [paymentDetailsInput]);

  const setEditMode = (value: boolean) => {
    dispatch({
      type: Actions.SetInvoicePaymentDetailsMode,
      payload: value
    });
    setEditable(value);
    if (!value) {
      const errors = { ...state.selectedInvoice?.errors };
      delete errors.isPaymentDetailsInEditMode;
      dispatch({
        type: Actions.SetInvoiceErrors,
        payload: {
          ...errors
        }
      });
    }
  };

  const resetInputFieldsValue = (data: IPaymentDetails) => {
    setAddressName(data.contact_address_name);
    setAddress(data.contact_address);
    setPhone(data.contact_phone);
    setFax(data.contact_fax);
    setVat(data.contact_vat);
    setBankName(data.bank_name);
    setBankAffiliate(data.bank_affiliate);
    setBankAddress(data.bank_address);
    setBankBic(data.bank_bic);
    setBankIban(data.bank_iban);
  };

  const shouldEditPage = () => {
    let result = false;
    const invoiceStatus = state.selectedInvoice?.status;
    if (
      canEdit &&
      (invoiceStatus === InvoiceStatus.New || invoiceStatus === InvoiceStatus.Pending)
    ) {
      result = true;
    }

    return result;
  };

  const handleCancelEdit = () => {
    resetInputFieldsValue(originalPaymentDetails);
    setFormErrors(defaultErrorsObj);
    setEditMode(false);
  };

  const handleSave = () => {
    let hasErrors = false;
    if (!isValidValue(addressName)) {
      hasErrors = true;
    }
    if (!isValidValue(address)) {
      hasErrors = true;
    }
    if (!isValidValue(phone)) {
      hasErrors = true;
    }
    if (!isValidPhoneFormat(phone)) {
      hasErrors = true;
    }
    if (!isValidValue(fax)) {
      hasErrors = true;
    }
    if (!isValidPhoneFormat(fax)) {
      hasErrors = true;
    }
    if (!isValidValue(vat)) {
      hasErrors = true;
    }
    if (!isValidValue(bankName)) {
      hasErrors = true;
    }
    if (!isValidValue(bankAffiliate)) {
      hasErrors = true;
    }
    if (!isValidValue(bankAddress)) {
      hasErrors = true;
    }
    if (!isValidValue(bankBic)) {
      hasErrors = true;
    }
    if (!isValidValue(bankIban)) {
      hasErrors = true;
    }
    if (!hasErrors) {
      if (!hasPaymentDetailsChanged()) {
        dispatch({
          type: Actions.ShowMessage,
          payload: {
            severity: 'warning',
            text: `There is no new information to save`
          }
        });
      } else {
        addPaymentDetails({
          variables: {
            input: {
              contact_address_name: addressName.trim(),
              contact_address: address.trim(),
              contact_phone: phone.trim(),
              contact_fax: fax.trim(),
              contact_vat: vat.trim(),
              bank_name: bankName.trim(),
              bank_affiliate: bankAffiliate.trim(),
              bank_address: bankAddress.trim(),
              bank_bic: bankBic.trim(),
              bank_iban: bankIban.trim()
            }
          },
          update(_, { data }) {
            if (data?.result) {
              setOriginalPaymentDetails(data.result);
              dispatch({
                type: Actions.SetInvoicePaymentDetails,
                payload: data.result
              });
              dispatch({
                type: Actions.ShowMessage,
                payload: {
                  severity: 'success',
                  text: `You have successfully added new payment details`
                }
              });
              if (updateDetails) {
                updateDetails(data.result);
              }
              setEditMode(false);
            }
          }
        }).catch((e) =>
          dispatch({
            type: Actions.ShowMessage,
            payload: {
              severity: 'error',
              text: e.message,
              autoHide: 6000
            }
          })
        );
      }
    }
  };

  const hasPaymentDetailsChanged = () => {
    if (originalPaymentDetails.contact_address_name.trim() !== addressName.trim()) {
      return true;
    }
    if (originalPaymentDetails.contact_address.trim() !== address.trim()) {
      return true;
    }
    if (originalPaymentDetails.contact_phone.trim() !== phone.trim()) {
      return true;
    }
    if (originalPaymentDetails.contact_fax.trim() !== fax.trim()) {
      return true;
    }
    if (originalPaymentDetails.contact_vat.trim() !== vat.trim()) {
      return true;
    }
    if (originalPaymentDetails.bank_name.trim() !== bankName.trim()) {
      return true;
    }
    if (originalPaymentDetails.bank_affiliate.trim() !== bankAffiliate.trim()) {
      return true;
    }
    if (originalPaymentDetails.bank_address.trim() !== bankAddress.trim()) {
      return true;
    }
    if (originalPaymentDetails.bank_bic.trim() !== bankBic.trim()) {
      return true;
    }
    if (originalPaymentDetails.bank_iban.trim() !== bankIban.trim()) {
      return true;
    }
    return false;
  };

  const isValidValue = (value: string) => {
    return value.trim() && value.trim().length >= 2 ? true : false;
  };

  const isValidPhoneFormat = (value: string) => {
    return value.trim()[0] === '+';
  };

  const handleAddressNameChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const { value } = event.currentTarget;
    if (!isValidValue(value)) {
      setFormErrors({
        ...formErrors,
        contactAddressName: ErrorTexts.contactAddressName
      });
    } else {
      setFormErrors({ ...formErrors, contactAddressName: null });
    }
    setAddressName(value);
  };

  const handleAddressChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const { value } = event.currentTarget;
    if (!isValidValue(value)) {
      setFormErrors({
        ...formErrors,
        contactAddress: ErrorTexts.contactAddress
      });
    } else {
      setFormErrors({ ...formErrors, contactAddress: null });
    }
    setAddress(value);
  };

  const handlePhoneChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const { value } = event.currentTarget;
    if (!isValidValue(value)) {
      setFormErrors({
        ...formErrors,
        contactPhone: ErrorTexts.contactPhone
      });
    } else if (!isValidPhoneFormat(value)) {
      setFormErrors({
        ...formErrors,
        contactPhone: ErrorTexts.contactPhoneFormat
      });
    } else {
      setFormErrors({ ...formErrors, contactPhone: null });
    }
    setPhone(value);
  };

  const handleFaxChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const { value } = event.currentTarget;
    if (!isValidValue(value)) {
      setFormErrors({
        ...formErrors,
        contactFax: ErrorTexts.contactFax
      });
    } else if (!isValidPhoneFormat(value)) {
      setFormErrors({
        ...formErrors,
        contactFax: ErrorTexts.contactFaxFormat
      });
    } else {
      setFormErrors({ ...formErrors, contactFax: null });
    }
    setFax(value);
  };

  const handleVatChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const { value } = event.currentTarget;
    if (!isValidValue(value)) {
      setFormErrors({
        ...formErrors,
        contactVat: ErrorTexts.contactVat
      });
    } else {
      setFormErrors({ ...formErrors, contactVat: null });
    }
    setVat(value);
  };

  const handleBankNameChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const { value } = event.currentTarget;
    if (!isValidValue(value)) {
      setFormErrors({
        ...formErrors,
        bankName: ErrorTexts.bankName
      });
    } else {
      setFormErrors({ ...formErrors, bankName: null });
    }
    setBankName(value);
  };

  const handleBankAffiliateChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const { value } = event.currentTarget;
    if (!isValidValue(value)) {
      setFormErrors({
        ...formErrors,
        bankAffiliate: ErrorTexts.bankAffiliate
      });
    } else {
      setFormErrors({ ...formErrors, bankAffiliate: null });
    }
    setBankAffiliate(value);
  };

  const handleBankAddressChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const { value } = event.currentTarget;
    if (!isValidValue(value)) {
      setFormErrors({
        ...formErrors,
        bankAddress: ErrorTexts.bankAddress
      });
    } else {
      setFormErrors({ ...formErrors, bankAddress: null });
    }
    setBankAddress(value);
  };

  const handleBankBicChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const { value } = event.currentTarget;
    if (!isValidValue(value)) {
      setFormErrors({
        ...formErrors,
        bankBic: ErrorTexts.bankBic
      });
    } else {
      setFormErrors({ ...formErrors, bankBic: null });
    }
    setBankBic(value);
  };

  const handleBankIbanChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const { value } = event.currentTarget;
    if (!isValidValue(value)) {
      setFormErrors({
        ...formErrors,
        bankIban: ErrorTexts.bankIban
      });
    } else {
      setFormErrors({ ...formErrors, bankIban: null });
    }
    setBankIban(value);
  };

  return (
    <Box>
      <Box sx={{ textAlign: 'right', width: '100%', marginTop: '28px' }}>
        {state.selectedInvoice?.errors.isPaymentDetailsInEditMode && (
          <span style={{ color: 'red', marginRight: 20 }}>
            Exit edit mode before going to the next step
          </span>
        )}
        {editable && (
          <Button
            id="invoice-wizard-payment-details-save-button"
            data-testid="invoice-wizard-payment-details-save-button"
            onClick={handleSave}
            disabled={addPaymentDetailsResult.loading}
          >
            <Save
              style={{
                fontSize: 'medium',
                color: Colors.main,
                marginRight: 3
              }}
            />
            SAVE
          </Button>
        )}
        {editable && (
          <Button
            id="invoice-wizard-payment-details-cancel-button"
            data-testid="invoice-wizard-payment-details-cancel-button"
            onClick={handleCancelEdit}
            disabled={addPaymentDetailsResult.loading}
          >
            <Close
              style={{
                fontSize: 'medium',
                color: 'red',
                marginRight: 3
              }}
            />
            CANCEL
          </Button>
        )}

        {shouldEditPage() && (
          <EditButton
            id="invoice-wizard-payment-details-edit-button"
            data-testid="invoice-wizard-payment-details-edit-button"
            onClick={() => setEditMode(true)}
            disabled={editable}
            editButtonColor={editable ? Colors.gray : Colors.skyBlue}
          />
        )}
      </Box>
      <Box
        sx={{
          display: 'grid',
          gridTemplateColumns: 'repeat(2, minmax(0, 1fr))',
          gap: '30px 8.33%',
          padding: '20px 10.4% 0px 9.17%'
        }}
      >
        <Box sx={{ ...predefinedFonts.Bold['14px22px'], color: Colors.main }}>CONTACT DETAILS</Box>
        <Box sx={{ ...predefinedFonts.Bold['14px22px'], color: Colors.main }}>
          BANK ACCOUNT DETAILS
        </Box>
        <InputComponent
          id="invoice-wizard-payment-details-address-name"
          data-testid="invoice-wizard-payment-details-address-name-input-label"
          label="Co.address Name"
          required
          value={addressName}
          onChange={handleAddressNameChange}
          disabled={!editable || addPaymentDetailsResult.loading}
          errorMsg={formErrors.contactAddressName ?? undefined}
          startAdornment={
            <HomeWork
              sx={{
                color: getLabelColor(editable),
                ml: '5px'
              }}
            />
          }
        />
        <InputComponent
          id="invoice-wizard-payment-details-bank-name"
          data-testid="invoice-wizard-payment-details-bank-name-input-label"
          label="Bank"
          required
          value={bankName}
          onChange={handleBankNameChange}
          disabled={!editable || addPaymentDetailsResult.loading}
          errorMsg={formErrors.bankName ?? undefined}
        />
        <InputComponent
          id="invoice-wizard-payment-details-address"
          data-testid="invoice-wizard-payment-details-address-input-label"
          label="Address"
          required
          value={address}
          onChange={handleAddressChange}
          disabled={!editable || addPaymentDetailsResult.loading}
          errorMsg={formErrors.contactAddress ?? undefined}
          startAdornment={
            <LocationOn
              sx={{
                color: getLabelColor(editable)
              }}
            />
          }
        />
        <InputComponent
          id="invoice-wizard-payment-details-bank-affiliate"
          data-testid="invoice-wizard-payment-details-bank-affiliate-input-label"
          label="Affiliate"
          required
          value={bankAffiliate}
          onChange={handleBankAffiliateChange}
          disabled={!editable || addPaymentDetailsResult.loading}
          errorMsg={formErrors.bankAffiliate ?? undefined}
        />
        <InputComponent
          id="invoice-wizard-payment-details-phone"
          data-testid="invoice-wizard-payment-details-phone-input-label"
          label="Phone Number"
          required
          value={phone}
          onChange={handlePhoneChange}
          disabled={!editable || addPaymentDetailsResult.loading}
          errorMsg={formErrors.contactPhone ?? undefined}
          startAdornment={
            <Call
              sx={{
                color: getLabelColor(editable)
              }}
            />
          }
        />
        <InputComponent
          id="invoice-wizard-payment-details-bank-address"
          data-testid="invoice-wizard-payment-details-bank-address-input-label"
          label="Address"
          required
          value={bankAddress}
          onChange={handleBankAddressChange}
          disabled={!editable || addPaymentDetailsResult.loading}
          errorMsg={formErrors.bankAddress ?? undefined}
        />
        <InputComponent
          id="invoice-wizard-payment-details-fax"
          data-testid="invoice-wizard-payment-details-fax-input-label"
          label="Fax (direct)"
          required
          value={fax}
          onChange={handleFaxChange}
          disabled={!editable || addPaymentDetailsResult.loading}
          errorMsg={formErrors.contactFax ?? undefined}
          startAdornment={
            <Print
              sx={{
                color: getLabelColor(editable)
              }}
            />
          }
        />
        <InputComponent
          id="invoice-wizard-payment-details-bank-bic"
          data-testid="invoice-wizard-payment-details-bank-bic-input-label"
          label="BIC"
          required
          value={bankBic}
          onChange={handleBankBicChange}
          disabled={!editable || addPaymentDetailsResult.loading}
          errorMsg={formErrors.bankBic ?? undefined}
        />
        <InputComponent
          id="invoice-wizard-payment-details-vat"
          data-testid="invoice-wizard-payment-details-vat-input-label"
          label="VAT Number"
          required
          value={vat}
          onChange={handleVatChange}
          disabled={!editable || addPaymentDetailsResult.loading}
          errorMsg={formErrors.contactVat ?? undefined}
          startAdornment={
            <Receipt
              sx={{
                color: getLabelColor(editable)
              }}
            />
          }
        />
        <InputComponent
          id="invoice-wizard-payment-details-bank-iban"
          data-testid="invoice-wizard-payment-details-bank-iban-input-label"
          label="IBAN"
          required
          value={bankIban}
          onChange={handleBankIbanChange}
          disabled={!editable || addPaymentDetailsResult.loading}
          errorMsg={formErrors.bankIban ?? undefined}
        />
      </Box>
    </Box>
  );
};

export default PaymentDetailsStep;
